import * as actionTypes from 'js/constants/actionTypes';
import { SCHEMA_TYPE_AI_TOOLS } from 'js/schemas/aiTool';

export interface AITool {
  id: number;
  key: string;
  name: string;
  description?: string;
  kind: 'nexset_read' | 'nexset_action' | 'custom_tool';
  status: 'init' | 'paused' | 'active';
  data_set_id?: number;
  data_source_id?: number;
  flow_node_id?: number;
  origin_node_id?: number;
  org_id?: number;
  owner_id?: number;
  // Tool definition stored directly (versionless)
  tool_definition?: Record<string, any>;
  // Hashes for change detection
  contract_hash?: string;
  schema_hash?: string;
  record_schema_fingerprint?: string;
  tags?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface AIToolsState {
  byId: Record<number, AITool>;
  allIds: number[];
  loading: boolean;
  error: string | null;
  lastUpdated: number | null;
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  } | null;
}

const initialState: AIToolsState = {
  byId: {},
  allIds: [],
  loading: false,
  error: null,
  lastUpdated: null,
  pagination: null,
};

export default function aiToolsReducer(
  state: AIToolsState = initialState,
  action: any,
): AIToolsState {
  switch (action.type) {
    case actionTypes.GET_AI_TOOLS_REQUEST:
    case actionTypes.GET_AI_TOOL_REQUEST:
    case actionTypes.CREATE_AI_TOOL_REQUEST:
    case actionTypes.UPDATE_AI_TOOL_REQUEST:
    case actionTypes.DELETE_AI_TOOL_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.GET_AI_TOOLS_SUCCESS: {
      const tools = action?.entities?.[SCHEMA_TYPE_AI_TOOLS] || {};
      const ids = action?.result || [];
      const shouldAppend = action.requestInfo?.append;
      const mergedIds = shouldAppend
        ? [...state.allIds, ...ids.filter((id) => !state.allIds.includes(id))]
        : ids;

      // Extract pagination metadata
      const rspHeaders = action.rspHeaders || {};
      const totalItems = Number(rspHeaders['X-Total-Count'] || ids.length);
      const currentPage = Number(rspHeaders['X-Current-Page'] || action.requestInfo?.page || 1);
      const pageSize = Number(action.requestInfo?.limit || 50);
      const totalPages = Number(
        rspHeaders['X-Total-Page-Count'] || Math.ceil(totalItems / pageSize) || 1,
      );

      return {
        ...state,
        byId: { ...state.byId, ...tools },
        allIds: mergedIds,
        loading: false,
        lastUpdated: Date.now(),
        pagination: {
          totalItems,
          totalPages,
          currentPage,
          pageSize,
        },
      };
    }

    case actionTypes.GET_AI_TOOL_SUCCESS:
    case actionTypes.CREATE_AI_TOOL_SUCCESS:
    case actionTypes.UPDATE_AI_TOOL_SUCCESS: {
      const tool = action.response?.entities?.[SCHEMA_TYPE_AI_TOOLS] || {};
      const toolId = action.response?.result;
      return {
        ...state,
        byId: { ...state.byId, ...tool },
        allIds: state.allIds.includes(toolId) ? state.allIds : [...state.allIds, toolId],
        loading: false,
        lastUpdated: Date.now(),
      };
    }

    case actionTypes.DELETE_AI_TOOL_SUCCESS: {
      const deletedId = action.requestInfo?.id;
      const { [deletedId]: removed, ...remainingById } = state.byId;
      return {
        ...state,
        byId: remainingById,
        allIds: state.allIds.filter((id) => id !== deletedId),
        loading: false,
        lastUpdated: Date.now(),
      };
    }

    case actionTypes.GET_AI_TOOLS_FAILURE:
    case actionTypes.GET_AI_TOOL_FAILURE:
    case actionTypes.CREATE_AI_TOOL_FAILURE:
    case actionTypes.UPDATE_AI_TOOL_FAILURE:
    case actionTypes.DELETE_AI_TOOL_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error?.message || 'An error occurred',
      };

    default:
      return state;
  }
}
